import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminRuleVersionsResponse } from '@kkp-fraud/shared';
import { COOKIE_MODE, api } from '../api';
import type { AuditEntry } from '../audit';
import { caseLink } from '../cases';
import { DEMO } from '../demo';
import { useShell } from '../components/AppLayout';
import { When } from '../components/When';
import { DecisionsTab } from '../components/DecisionsTab';
import { LoadError } from '../components/LoadError';
import { PageHead, SubTabs, Tip } from '../components/PageHead';
import { t } from '../i18n';

/**
 * Log — the three back-looking records from the mockup.
 *
 * Decisions, rule changes and access all come through `api`: `GET /admin/decisions`,
 * `GET /admin/rules/versions` and, in cookie mode, the admin service's `GET /api/audit`.
 * Password mode has no access trail at all, so there the tab says so in one line — unless
 * the demo data layer (`VITE_DEMO=1`) is on.
 */

type TabId = 'decisions' | 'rule-changes' | 'access';

/**
 * Read-only release history. Rollback lives on Rule Management, where the draft it would
 * overwrite is also on screen — offering "activate" from an audit log invites releasing a
 * version without seeing what it changes.
 */
function RuleChangeLog() {
  const { settings } = useShell();
  const [versions, setVersions] = useState<AdminRuleVersionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Until the first answer there is no count and no "nothing yet" to claim (QA-M-05). */
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setVersions(await api.versions(settings));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [settings.baseUrl]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const rows = versions?.versions ?? [];

  return (
    <section className="card">
      <header className="card-head">
        <h2>{t('log.tab.rules')}</h2>
        <Tip>{t('log.releases.note')}</Tip>
        {versions && (
          <span className="chip" data-testid="log-releases-count">
            {t(rows.length === 1 ? 'log.releases.count.one' : 'log.releases.count.other', { n: rows.length })}
          </span>
        )}
        <span className="spacer" />
        <button type="button" className="btn" onClick={() => void refresh()} disabled={loading}>
          {loading ? t('btn.loading.label') : t('btn.refresh.label')}
        </button>
      </header>
      {error && (
        <div className="card-body">
          <LoadError message={t('log.error.releases')} detail={error} testId="log-releases-error" />
        </div>
      )}
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 130 }}>{t('log.col.version')}</th>
              <th style={{ width: 170 }}>{t('log.col.released')}</th>
              <th style={{ width: 200 }}>{t('log.col.by')}</th>
              <th style={{ width: 110 }}>{t('log.col.contents')}</th>
              <th>{t('log.col.comment')}</th>
            </tr>
          </thead>
          <tbody>
            {!versions && loading && (
              <tr>
                <td colSpan={5} className="empty" role="status" data-testid="log-releases-loading">
                  {t('btn.loading.label')}
                </td>
              </tr>
            )}
            {versions && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  {t('log.releases.empty')}
                </td>
              </tr>
            )}
            {rows.map((v) => (
              <tr key={v.version} data-testid={`log-version-${v.version}`}>
                <td className="mono nowrap">
                  {v.version} {v.isActive && <span className="chip ok">{t('log.version.active.chip')}</span>}
                </td>
                <td className="small nowrap">
                  <When iso={v.savedUtc} />
                </td>
                <td className="small">{v.actor}</td>
                <td className="small mono">
                  {v.ruleBasedEnabled} RB · {v.patternBasedEnabled} PB
                </td>
                <td className="small">{v.comment ?? <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** The admin service's verbs, as an operator reads them. Unknown verbs are shown as sent. */
const ACCESS_WHAT: Record<string, string> = {
  'login.success': 'log.access.what.signin',
  'login.failure': 'log.access.what.signin-failed',
  logout: 'log.access.what.signout',
  'access.denied': 'log.access.what.denied',
  'proposal.created': 'log.access.what.proposed',
  'proposal.validated': 'log.access.what.validated',
  'proposal.replayed': 'log.access.what.replayed',
  'proposal.approved': 'log.access.what.approved',
  'proposal.published': 'log.access.what.published',
  // The console's release path while maker-checker is dropped: publish and roll back directly.
  'rules.published': 'log.access.what.published',
  'rules.rolledback': 'log.access.what.rolledback',
  'report.exported': 'log.access.what.exported',
  // R8 users — user management and the sessions it ends.
  'user.created': 'users.log.user_created',
  'user.updated': 'users.log.user_updated',
  'user.deleted': 'users.log.user_deleted',
  'user.password_reset': 'users.log.user_password_reset',
  'user.password_changed': 'users.log.user_password_changed',
  'user.password_change_failed': 'users.log.user_password_change_failed',
  'session.revoked': 'users.log.session_revoked',
  // R9 cases (labels kept in the users.log namespace at the lead's request).
  'case.created': 'users.log.case_created',
  'case.updated': 'users.log.case_updated',
  'case.transition': 'users.log.case_transition',
  'case.note.added': 'users.log.case_note_added',
  'case.note.edited': 'users.log.case_note_edited',
  'case.note.deleted': 'users.log.case_note_deleted',
  // UX review P2-9: every verb the admin service writes reads as words, not as the code it sends.
  'master.created': 'log.access.what.master_created',
  'master.updated': 'log.access.what.master_updated',
  'master.deleted': 'log.access.what.master_deleted',
  'master.imported': 'log.access.what.master_imported',
  'master.item.added': 'log.access.what.master_item_added',
  'master.item.updated': 'log.access.what.master_item_updated',
  'master.item.removed': 'log.access.what.master_item_removed',
  'master.template.created': 'log.access.what.template_created',
  'master.template.updated': 'log.access.what.template_updated',
  'master.template.deleted': 'log.access.what.template_deleted',
  'master.template.assigned': 'log.access.what.template_assigned',
  'webhook.created': 'log.access.what.webhook_created',
  'webhook.updated': 'log.access.what.webhook_updated',
  'webhook.deleted': 'log.access.what.webhook_deleted',
  'webhook.test_sent': 'log.access.what.webhook_tested',
  'alerts.test_sent': 'log.access.what.alert_tested',
  // Wave 4 API clients
  'apiclient.created': 'log.access.what.apiclient_created',
  'apiclient.updated': 'log.access.what.apiclient_updated',
  'apiclient.rotated': 'log.access.what.apiclient_rotated',
  'apiclient.revoked': 'log.access.what.apiclient_revoked',
};

const ACCESS_CHIP: Record<string, string> = {
  'login.failure': 'warn',
  'access.denied': 'bad',
  'proposal.approved': 'ok',
  'proposal.published': 'ok',
  'rules.published': 'ok',
  'rules.rolledback': 'warn',
  // R8 users
  'user.deleted': 'warn',
  'user.password_change_failed': 'warn',
  'session.revoked': 'warn',
  // Wave 4 API clients
  'apiclient.revoked': 'warn',
};

/**
 * R8 users: why a sign-in failed. The admin service sends the reason (and the lockout flag) to an
 * Admin only; everyone else gets a bare "sign-in failed" with no subject.
 */
const SIGNIN_REASONS = new Set(['unknown_user', 'wrong_password', 'locked', 'disabled', 'blank']);

function lockedNow(e: AuditEntry): boolean {
  return e.what === 'login.failure' && (e.details as { locked?: unknown } | undefined)?.locked === true;
}

/** A non-Admin gets `who: "unknown"` on a failed sign-in (the typed username may be a password). */
function hiddenWho(e: AuditEntry): boolean {
  return e.what === 'login.failure' && e.who === 'unknown';
}

function accessLabel(e: AuditEntry): string {
  if (lockedNow(e)) return t('users.log.login_locked');
  const key = ACCESS_WHAT[e.what];
  return key ? t(key) : e.what;
}

function accessSubject(e: AuditEntry): string | undefined {
  if (e.what === 'login.failure' && e.subject && SIGNIN_REASONS.has(e.subject)) return t(`users.log.reason.${e.subject}`);
  return e.subject;
}

/** A case event's case, as a link to it (UX review P1-3) — every `case.*` entry carries the card in `details`. */
function caseTarget(e: AuditEntry): string | null {
  if (!e.what.startsWith('case.') || !e.subject) return null;
  const card = (e.details as { cardId?: unknown } | undefined)?.cardId;
  return typeof card === 'string' && card ? caseLink(card, e.subject) : null;
}

/** The access log's text filter: who, what (as read and as sent) and subject. */
function accessMatches(e: AuditEntry, q: string): boolean {
  if (!q) return true;
  const hay = [hiddenWho(e) ? '' : e.who, accessLabel(e), e.what, accessSubject(e) ?? '', e.subject ?? ''].join('\n').toLowerCase();
  return hay.includes(q);
}

function AccessLog() {
  const { settings } = useShell();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q ? entries.filter((e) => accessMatches(e, q)) : entries;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await api.audit(settings, 200));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [settings]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section className="card" data-testid="access-log">
      <header className="card-head">
        <h2>{t('log.tab.access')}</h2>
        <span className="chip">{t(shown.length === 1 ? 'log.access.count.one' : 'log.access.count.other', { n: shown.length })}</span>
        <span className="spacer" />
        <input
          type="search"
          aria-label={t('log.access.search.a11y')}
          placeholder={t('log.access.search.placeholder')}
          style={{ width: 260 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="access-search"
        />
        <button type="button" className="btn" onClick={() => void refresh()} disabled={loading}>
          {loading ? t('btn.loading.label') : t('btn.refresh.label')}
        </button>
      </header>
      {error && (
        <div className="card-body">
          <LoadError message={t('log.error.access')} detail={error} testId="access-log-error" />
        </div>
      )}
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 170 }}>{t('log.access.col.when')}</th>
              <th style={{ width: 260 }}>{t('log.col.user')}</th>
              <th style={{ width: 200 }}>{t('log.access.col.what')}</th>
              <th>{t('log.access.col.subject')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className="empty">
                  {entries.length === 0 ? t('log.access.empty') : t('log.search.none')}
                </td>
              </tr>
            )}
            {shown.map((e) => {
              const subject = accessSubject(e);
              const toCase = caseTarget(e);
              return (
                <tr key={e.id} data-testid={`access-${e.id}`}>
                  <td className="small nowrap">
                    <When iso={e.whenUtc} />
                  </td>
                  <td className="small mono">{hiddenWho(e) ? <span className="muted">—</span> : e.who}</td>
                  <td>
                    <span className={`chip ${lockedNow(e) ? 'bad' : (ACCESS_CHIP[e.what] ?? '')}`.trim()}>{accessLabel(e)}</span>
                  </td>
                  <td className={subject === e.subject ? 'small mono' : 'small'}>
                    {toCase ? (
                      <Link to={toCase} data-testid={`access-case-${e.id}`}>
                        {subject}
                      </Link>
                    ) : (
                      (subject ?? <span className="muted">—</span>)
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function LogScreen() {
  const { settings } = useShell();
  const [tab, setTab] = useState<TabId>('decisions');

  return (
    <>
      <PageHead title={t('screen.log.title')} lead={t('screen.log.console.lead')} />

      <SubTabs
        label={t('screen.log.title')}
        active={tab}
        onSelect={(id) => setTab(id as TabId)}
        tabs={[
          { id: 'decisions', label: t('tab.decisions.label') },
          { id: 'rule-changes', label: t('tab.rule-changes.label') },
          { id: 'access', label: t('tab.access.label') },
        ]}
      />

      {tab === 'decisions' && <DecisionsTab settings={settings} />}
      {tab === 'rule-changes' && (
        <div className="stack">
          {/* Proposals are not shown while maker-checker is dropped: every change is a release. */}
          <RuleChangeLog />
        </div>
      )}
      {tab === 'access' && (COOKIE_MODE || DEMO) && <AccessLog />}
      {tab === 'access' && !COOKIE_MODE && !DEMO && (
        <section className="card">
          <header className="card-head">
            <h2>{t('log.tab.access')}</h2>
          </header>
          <div className="card-body">
            {/* One line. An audit screen with nothing in it says so and stops. */}
            <div className="empty">{t('log.access.notbuilt.what')}</div>
          </div>
        </section>
      )}
    </>
  );
}
